import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import LogoutIcon from '@mui/icons-material/Logout';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import sibcheImage from '/src/assets/images/Logo/sibche.png?url';
import recImage from '/src/assets/images/Logo/rectangle.png?url';
import { RootState, AppDispatch } from '../../store';
import apiClient from '../../api/apiClient';
import LogoutConfirmModal from './LogoutConfirmModal';
import { logout } from '../../Slice/auth/authSlice';
import NumberConverter from '../../helper/ConvertPersionNumberToEnglish';

// Styled Components
const StyledAppBar = styled(AppBar)`
  width: 100%;
  height: 56px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
`;

const StyledToolbar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding-left: 168px;
  padding-right: 168px;
  @media (max-width: 600px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const LogoImage = styled.img`
  height: 41px;
  width: auto;
`;

const SibcheImage = styled.img`
  height: 21px;
  width: auto;
`;

const RightIcon = styled.img`
  height: 20px;
  width: auto;
  margin-left: 4px;
`;

const LogoContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ProfileContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${blue[50]};
  }
`;

const ProfileText = styled('span')`
  font-family: 'IRANYekanXFaNum', sans-serif;
  font-size: 14px;
  color: ${blue[500]};
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

interface HeaderProps {
  buttonText: string;
  iconSrc: string;
}

const Header: React.FC<HeaderProps> = ({ buttonText, iconSrc }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { mobile, token } = useSelector((state: RootState) => state.auth);

  console.log('Auth State:', { mobile, token }); // Debug Redux state

  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);

    try {
      // Invalidates the token server side so it cannot be reused. The local
      // session is cleared either way: if this call fails the user still
      // expects to be signed out, and leaving them on a signed in screen
      // would be worse than a token that expires on its own.
      await apiClient.post('/v2/publisher/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      setLoggingOut(false);
      setLogoutModalOpen(false);
      dispatch(logout());
      navigate('/');
    }
  };

  return (
    <StyledAppBar
     style={{ 
       backgroundColor: '#ffffff',
       boxShadow: '0 0px 4px rgba(0, 0, 0, 0.1)',
       height: '80px',
      }}
     position="static">
      <StyledToolbar>
        {/* Button Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            marginLeft: '20%',
            '@media (max-width: 600px)': {
              marginLeft: '10%',
            },
          }}
        >
          <Button
            color="inherit"
            onClick={() => (window.location.href = 'https://www.sibche.com')}
            sx={{
              fontFamily: 'IRANYekanXFaNum, sans-serif',
              fontSize: '12px',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              maxWidth: '150px',
              color: blue[500],
              '&:hover': {
                backgroundColor: blue[50],
              },
            }}
          >
            {buttonText}
            <RightIcon src={iconSrc} alt="right icon" />
          </Button>
        </Box>

        <LogoContainer
          sx={{
            marginRight: token && mobile ? '10%' : '20%',
            '@media (max-width: 600px)': {
              marginRight: '10%',
            },
          }}
        >
          <SibcheImage src={sibcheImage} alt="sibche logo" />
          <LogoImage src={recImage} alt="logo" />
          {token && mobile && (
            <Tooltip title="خروج از حساب کاربری" arrow>
              <ProfileContainer onClick={() => setLogoutModalOpen(true)}>
                <ProfileText>
                  {NumberConverter.toPersianNumber(mobile)}
                </ProfileText>
                <LogoutIcon
                  sx={{
                    color: blue[500],
                    fontSize: '18px',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              </ProfileContainer>
            </Tooltip>
          )}
        </LogoContainer>
      </StyledToolbar>

      <LogoutConfirmModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleLogout}
        loading={loggingOut}
      />
    </StyledAppBar>
  );
};

export default Header;
